import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, apiClient } from '../auth/AuthContext';
import PricingRulesGrid from './PricingRulesGrid';
import {
  blockToRulePayload,
  PricingBlock,
  PricingRuleRecord,
  ruleEffectiveFrom,
} from './pricingRulesUtils';

interface User {
  id: string;
  username: string;
  email: string;
  role: 'admin' | 'manager' | 'tenant';
  buildingId: string;
  createdAt: string;
  updatedAt: string;
}

interface NewUser {
  username: string;
  email: string;
  password: string;
  role: 'admin' | 'manager' | 'tenant';
}

const HOLIDAY_STORAGE_KEY = (buildingId: string) => `pricing-holidays-${buildingId}`;

function saveHolidayDates(buildingId: string, dates: string[]) {
  localStorage.setItem(HOLIDAY_STORAGE_KEY(buildingId), JSON.stringify(dates));
}

const AdminPage: React.FC = () => {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState<'users' | 'pricing'>('users');
  const [users, setUsers] = useState<User[]>([]);
  const [pricingRules, setPricingRules] = useState<PricingRuleRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [pricingSaving, setPricingSaving] = useState(false);
  const [error, setError] = useState('');

  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState<NewUser>({
    username: '',
    email: '',
    password: '',
    role: 'tenant',
  });

  const fetchUsers = async () => {
    if (!token) return;

    try {
      setLoading(true);
      const data = await apiClient.get<User[]>('/admin/users');
      setUsers(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchPricingRules = async () => {
    if (!token || !user?.buildingId) return;

    try {
      setLoading(true);
      const data = await apiClient.get<PricingRuleRecord[]>(`/pricing/${user.buildingId}`);
      setPricingRules(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to fetch pricing rules');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    try {
      await apiClient.register({ ...newUser, buildingId: user?.buildingId });

      setNewUser({ username: '', email: '', password: '', role: 'tenant' });
      setShowAddUser(false);
      fetchUsers();
      setError('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    }
  };

  const handleSavePricing = useCallback(
    async (blocks: PricingBlock[], publicHolidayDates: string[], effectiveFrom: string) => {
      if (!token || !user?.buildingId) return;

      setPricingSaving(true);
      setError('');

      try {
        for (const rule of pricingRules) {
          if (ruleEffectiveFrom(rule) === effectiveFrom) {
            await apiClient.delete(`/pricing/${rule.id}?buildingId=${encodeURIComponent(user.buildingId)}`);
          }
        }

        for (const block of blocks) {
          await apiClient.post('/pricing', blockToRulePayload(block, user.buildingId, effectiveFrom));
        }

        saveHolidayDates(user.buildingId, publicHolidayDates);
        await fetchPricingRules();
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to save pricing rules';
        setError(message);
        throw err;
      } finally {
        setPricingSaving(false);
      }
    },
    [token, user?.buildingId, pricingRules],
  );

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    } else {
      fetchPricingRules();
    }
  }, [activeTab, token, user?.buildingId]);

  return (
    <div className="ah-shell">
      <div className="ah-panel">
        <div style={{ maxWidth: 1400, margin: '0 auto' }}>
          <div className="ah-card">
            <div style={{ marginBottom: 24 }}>
              <h1 className="ah-title">Admin Panel</h1>
              <p className="muted">Manage users and pricing for your building</p>
            </div>

            <div style={{ display: 'flex', gap: 16, marginBottom: 24, borderBottom: '1px solid var(--border)' }}>
              <button
                onClick={() => setActiveTab('users')}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '12px 0',
                  cursor: 'pointer',
                  fontSize: 16,
                  fontWeight: 500,
                  color: activeTab === 'users' ? 'var(--primary)' : 'var(--text)',
                  borderBottom: activeTab === 'users' ? '2px solid var(--primary)' : '2px solid transparent',
                }}
              >
                User Management
              </button>
              <button
                onClick={() => setActiveTab('pricing')}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '12px 0',
                  cursor: 'pointer',
                  fontSize: 16,
                  fontWeight: 500,
                  color: activeTab === 'pricing' ? 'var(--primary)' : 'var(--text)',
                  borderBottom: activeTab === 'pricing' ? '2px solid var(--primary)' : '2px solid transparent',
                }}
              >
                Pricing Rules
              </button>
            </div>

            {error && (
              <div
                style={{
                  color: '#e53e3e',
                  backgroundColor: '#fed7d7',
                  border: '1px solid #feb2b2',
                  borderRadius: 6,
                  padding: 16,
                  marginBottom: 24,
                }}
              >
                {error}
              </div>
            )}

            {activeTab === 'users' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                  <h2>Users</h2>
                  <button onClick={() => setShowAddUser(true)} className="ah-btn">
                    Add User
                  </button>
                </div>

                {showAddUser && (
                  <div className="ah-card" style={{ marginBottom: 24, backgroundColor: 'var(--surface-hover)' }}>
                    <h3 style={{ marginBottom: 16 }}>Add New User</h3>
                    <form onSubmit={handleAddUser}>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                          gap: 16,
                          marginBottom: 16,
                        }}
                      >
                        <input
                          type="text"
                          className="ah-input"
                          placeholder="Username"
                          value={newUser.username}
                          onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                          required
                        />
                        <input
                          type="email"
                          className="ah-input"
                          placeholder="Email"
                          value={newUser.email}
                          onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                          required
                        />
                        <input
                          type="password"
                          className="ah-input"
                          placeholder="Password"
                          value={newUser.password}
                          onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                          required
                        />
                        <select
                          className="ah-input"
                          value={newUser.role}
                          onChange={(e) =>
                            setNewUser({ ...newUser, role: e.target.value as 'admin' | 'manager' | 'tenant' })
                          }
                        >
                          <option value="tenant">Tenant</option>
                          <option value="manager">Manager</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="submit" className="ah-btn">
                          Add User
                        </button>
                        <button type="button" onClick={() => setShowAddUser(false)} className="ah-btn ah-btn-outline">
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {loading ? (
                  <div style={{ textAlign: 'center', padding: 40 }}>Loading users...</div>
                ) : (
                  <div className="ah-table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Username</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((u) => (
                          <tr key={u.id}>
                            <td>{u.username}</td>
                            <td>{u.email}</td>
                            <td>
                              <span
                                style={{
                                  background:
                                    u.role === 'admin' ? '#e53e3e' : u.role === 'manager' ? '#dd6b20' : '#38a169',
                                  color: 'white',
                                  padding: '2px 8px',
                                  borderRadius: 12,
                                  fontSize: 12,
                                  fontWeight: 500,
                                }}
                              >
                                {u.role.toUpperCase()}
                              </span>
                            </td>
                            <td>{new Date(u.createdAt).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'pricing' && user?.buildingId && (
              <PricingRulesGrid
                buildingId={user.buildingId}
                rules={pricingRules}
                loading={loading}
                saving={pricingSaving}
                onSave={handleSavePricing}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
